"use client";

/**
 * AppShell — The one grid that rules them all.
 *
 * Provides:
 * - Persistent sidebar (never remounts across navigation)
 * - Persistent glassy header with "Fülkit" + page name
 * - Toolbar slot for per-page buttons (via useToolbar hook)
 * - Consistent grid for both app and public pages
 *
 * Architecture:
 * - Authenticated: sidebar column + content column (header + scrollable content)
 * - Unauthenticated: content column only (header adapts, no sidebar)
 * - Mobile: no sidebar, bottom tab bar handled by MobileTabBar in layout
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "../lib/auth";
import { useVaultContext } from "../lib/vault";
import { isFileSystemAccessSupported } from "../lib/vault-local";
import { emitSignal } from "../lib/signal";
import { supabase } from "../lib/supabase";
import { useIsMobile } from "../lib/use-mobile";
import Sidebar from "./Sidebar";
import V2MigrationModal from "./V2MigrationModal";
import BannerSlot from "./BannerSlot";
import AnnouncementBanner from "./AnnouncementBanner";
import { markLearningKitVisited } from "../lib/learning-kit";

// ─── Toolbar Context ──────────────────────────────────────
const ToolbarContext = createContext({ setToolbar: () => {} });

export function useToolbar() {
  return useContext(ToolbarContext);
}

// ─── Page Names ───────────────────────────────────────────
const PAGE_NAMES = {
  "/": null,
  "/home": null,
  "/chat": "Chat",
  "/actions": "Actions",
  "/threads": "Threads",
  "/settings": "Settings",
  "/fabric": "Fabric",
  "/owner": "Owner",
  "/import": "Import",
  "/hum": "The Hum",
  "/landing": null,
  "/login": null,
  "/about": null,
  "/privacy": "Privacy",
  "/terms": "Terms",
  "/wtf": null,
  "/onboarding": null,
};

function getPageName(pathname) {
  if (PAGE_NAMES[pathname] !== undefined) return PAGE_NAMES[pathname];
  for (const [path, name] of Object.entries(PAGE_NAMES)) {
    if (path !== "/" && pathname.startsWith(path + "/")) return name;
  }
  return null;
}

// ─── AppShell ─────────────────────────────────────────────
export default function AppShell({ children }) {
  const { user, profile, fetchProfile, compactMode } = useAuth();
  const vaultCtx = useVaultContext();
  const isMobile = useIsMobile();
  const pathname = usePathname();
  const router = useRouter();
  const [toolbar, setToolbarRaw] = useState(null);
  const setToolbar = useCallback((content) => setToolbarRaw(content), []);

  // V2 Step 10d — returning-user migration trigger.
  // Fires when user has profile.onboarded=true but onboarding_completed_v2=false
  // AND is on Chromium desktop. Step 1 SQL backfilled all V1 users to true,
  // so this is primarily a safety net for future deploys / SQL-skipped envs.
  // Auto-detect silent path (V2 spec § Returning user migration paths row 1):
  // if directoryHandle is restored from IDB, silently set both flags + skip
  // the modal. Otherwise the modal renders and hard-blocks the UI.
  const [v2MigrationCompleted, setV2MigrationCompleted] = useState(false);
  const needsV2Migration = useMemo(
    () => Boolean(
      profile?.onboarded === true
      && profile?.onboarding_completed_v2 === false
      && isFileSystemAccessSupported()
    ),
    [profile?.onboarded, profile?.onboarding_completed_v2]
  );

  useEffect(() => {
    if (!needsV2Migration || v2MigrationCompleted || !user?.id || !profile?.id) return;
    if (vaultCtx.directoryHandle) {
      // Auto-detect silent path — handle restored from IDB, V1 local user with
      // missed Step 1 backfill. Silent migration: lockstep flags, no modal.
      let cancelled = false;
      (async () => {
        try {
          await vaultCtx.setMirrorFolderConnected(true);
          await supabase
            .from("profiles")
            .update({ onboarding_completed_v2: true })
            .eq("id", profile.id);
          if (cancelled) return;
          emitSignal("vault_returning_user_migration", "info", { auto_detected: true, had_handle: true });
          emitSignal("vault_returning_user_completed", "info", { auto_detected: true });
          setV2MigrationCompleted(true);
          await fetchProfile(user.id);
        } catch (err) {
          // Silent failure — surface for retry next session via vault_state_divergence
          // signal at vault.js init. Don't signal-spam here.
        }
      })();
      return () => { cancelled = true; };
    }
    // No handle in IDB — modal will render. Emit one signal per session
    // (gated by v2MigrationCompleted state).
    emitSignal("vault_returning_user_migration", "info", { auto_detected: false, had_handle: false });
  }, [needsV2Migration, vaultCtx.directoryHandle, v2MigrationCompleted, user?.id, profile?.id, vaultCtx, fetchProfile]);

  const showV2MigrationModal = needsV2Migration && !vaultCtx.directoryHandle && !v2MigrationCompleted;

  // ─── Global hotkeys ────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      // Don't intercept when typing in inputs
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.contentEditable === "true") return;

      const cmd = e.metaKey || e.ctrlKey;
      if (!cmd) return;

      if (e.key === "n") { e.preventDefault(); router.push("/chat"); return; }
      if (e.key === "j") { e.preventDefault(); router.push("/threads"); return; }
      if (e.key === "h") { e.preventDefault(); router.push("/home"); return; }
      if (e.shiftKey && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        // Learning Kit — mark try-tiny-fulkit card done. Helper dispatches
        // the fulkit:learning-kit-state-changed event so WelcomeKitCard
        // (mounted in this window) re-checks completion immediately
        // instead of waiting for next navigation.
        markLearningKitVisited("lk-opened-tiny-fulkit");
        // Popout opens clean — no ?conv= inheritance. Earlier behavior
        // ("popout inherits active conversation") leaked the main chat's
        // history into the popout, contaminating the first-impression of
        // the mini-Fülkit surface. If you want to push a specific chat to
        // the popout, use the explicit popout CTA inside ChatContent.
        window.open("/chat/popout", "fulkit-popout", "width=400,height=600,resizable=yes,scrollbars=yes");
        // Pivot main page to /home only if hotkey fired from the main /chat
        // page — chat moves to the side pane, main view is freed. Hotkey
        // from /chat/popout (rare — re-focuses self) or any other page
        // leaves navigation alone.
        if (pathname === "/chat") router.push("/home");
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user, router]);

  // Clear toolbar on navigation
  const prevPathRef = useRef(pathname);
  useEffect(() => {
    if (pathname !== prevPathRef.current) {
      setToolbarRaw(null);
      prevPathRef.current = pathname;
    }
  }, [pathname]);

  const pageName = getPageName(pathname);
  const isAuthenticated = !!user;
  const contextValue = useMemo(() => ({ setToolbar }), [setToolbar]);

  // Standalone pages — no shell, they handle their own layout
  const standalone = ["/landing", "/wtf"];
  const isStandalone = standalone.some(p => pathname === p || pathname.startsWith(p + "/"));
  if (!isAuthenticated || isStandalone) return <ToolbarContext.Provider value={contextValue}>{children}</ToolbarContext.Provider>;

  return (
    <ToolbarContext.Provider value={contextValue}>
      {showV2MigrationModal && <V2MigrationModal />}
      <div style={{
        display: "flex",
        width: "100%",
        height: "100dvh",
        overflow: "hidden",
        paddingBottom: isMobile && isAuthenticated ? "var(--tab-bar-height, 56px)" : "var(--trial-sticky-height, 0px)",
      }}>
        {/* Sidebar column — authenticated desktop only */}
        {isAuthenticated && !isMobile && <Sidebar />}

        {/* Content column */}
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* Glassy header — persistent, sticky feel */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-2)",
            padding: isMobile ? "calc(var(--space-1) + env(safe-area-inset-top, 0px)) var(--space-3) var(--space-2)" : "var(--space-2-5) var(--space-6)",
            minHeight: 44,
            background: "rgba(239, 237, 232, 0.6)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            borderBottom: "1px solid rgba(42, 40, 38, 0.08)",
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: "var(--font-size-base)",
              fontWeight: "var(--font-weight-black)",
              letterSpacing: "var(--letter-spacing-tight)",
              color: "var(--color-text)",
              marginTop: 4,
            }}>
              F{"\u00FC"}lkit
            </span>
            {!compactMode && !isMobile && pageName && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-dim)", fontWeight: "var(--font-weight-medium)" }}>/</span>
                <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", fontWeight: "var(--font-weight-semibold)" }}>{pageName}</span>
              </span>
            )}
            <div id="appshell-toolbar" style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: isMobile ? "var(--space-3)" : "var(--space-2)", flexShrink: 0 }}>
              {toolbar}
            </div>
            {/* Public pages bypass AppShell entirely — no unauthenticated header needed */}
          </div>

          {/* Page content — fills remaining space, pages manage own scroll.
              BannerSlot + AnnouncementBanner mount here as the canonical
              top-of-content slot for all authenticated pages. Both self-gate
              on render conditions, so the wrapping div has zero layout cost
              when neither has anything to show.
              Glass background mirrors the AppShell header pattern — gives a
              tinted-translucent character. True frosted-glass-over-scrolling-
              content was attempted via a sticky-over-AppShell-scroll proof
              on /home (commit 403b65ca, reverted next commit) but broke the
              page's scroll. Sticky-glass requires deeper restructure than a
              two-edit proof. Tracked as open future-idea. */}
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{
              padding: "var(--space-3) var(--space-5) 0",
              flexShrink: 0,
              background: "rgba(239, 237, 232, 0.6)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
            }}>
              <BannerSlot />
              <AnnouncementBanner />
            </div>
            {children}
          </div>
        </div>
      </div>
    </ToolbarContext.Provider>
  );
}
